import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button } from 'components/ui/button';
import { Icon } from 'components/ui/icon';
import { Controller, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import CURRENCY from 'utils/currency';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
type Key = (typeof KEYS)[number];

interface IProps {
  setAmount: Dispatch<SetStateAction<number>>;
  amount: number;
  isUnpaidTicketFlow?: boolean;
}

const keyButtonClass =
  'h-16 text-2xl border border-gray-blue-20 bg-white text-gray-blue-90 rounded-2xl shadow-sm hover:enabled:bg-gray-blue-2 hover:enabled:-translate-y-px active:enabled:scale-[0.97] transition-all duration-150 md:text-xl';

const NumericKeypad = ({ setAmount, amount, isUnpaidTicketFlow }: IProps): JSX.Element => {
  const [value, setValue] = useState<string>(CURRENCY.convertToMainUnit(amount) || '');
  const isUpdatingFromAmountRef = useRef(false);

  const handleKey = useCallback((key: Key) => {
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    setValue((prev) => {
      let numeric = prev.replace(/\D/g, '');

      if (key === 'back') {
        numeric = numeric.slice(0, -1);
      } else if (/\d/.test(key)) {
        numeric = numeric + key;
      } else {
        return prev;
      }

      if (numeric === '') return '';

      const number = parseFloat(numeric) / 100;
      return number.toFixed(2);
    });
  }, []);

  useEffect(() => {
    isUpdatingFromAmountRef.current = true;
    setValue(CURRENCY.convertToMainUnit(amount));
    setTimeout(() => {
      isUpdatingFromAmountRef.current = false;
    }, 0);
  }, [amount]);

  const display = `$${value || '0'}`;

  useEffect(() => {
    if (!isUpdatingFromAmountRef.current) {
      if (value !== '' && value !== '0.00' && value) {
        const temp = Math.round(parseFloat(value.replace('$', '')) * 100);
        setAmount(temp);
      } else {
        setAmount(0);
      }
    }
  }, [value]);

  const { control } = useForm();

  const rightToLeftFormatter = (input: string) => {
    const raw = input.replace(/\D/g, '');

    if (raw === '') {
      setValue('');
      return '';
    }
    const numeric = parseInt(raw, 10);
    const formatted = (numeric / 100).toFixed(2);

    setValue(formatted);
    return `$${formatted}`;
  };

  return (
    <div className="w-full pt-4 px-4">
      <Controller
        control={control}
        name="originalAmount"
        rules={{ required: true }}
        render={({ field }) => (
          <NumberFormat
            name="originalAmount"
            id="originalAmount"
            fixedDecimalScale
            allowNegative={false}
            prefix="$"
            decimalScale={2}
            className="block w-full text-right border border-gray-blue-20 rounded-2xl h-16 text-4xl px-4 mb-4 shadow-sm focus:outline-none focus:border-green-400 md:h-14 md:text-3xl"
            value={display}
            format={rightToLeftFormatter}
            placeholder="$5000.00"
            getInputRef={field.ref}
            disabled={isUnpaidTicketFlow}
          />
        )}
      />

      <div className="grid grid-cols-3 gap-2 mb-3">
        {KEYS.map((item) => (
          <Button
            key={item}
            type="button"
            variant="outline"
            className={keyButtonClass}
            onClick={() => handleKey(item)}
            disabled={isUnpaidTicketFlow}
          >
            {item}
          </Button>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={() => handleKey('back')}
          disabled={isUnpaidTicketFlow}
          className={keyButtonClass}
          aria-label="Backspace"
        >
          <Icon name="chevron-left" size={20} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => handleKey('0')}
          disabled={isUnpaidTicketFlow}
          className={keyButtonClass}
        >
          0
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => handleKey('.')}
          disabled={isUnpaidTicketFlow}
          className={keyButtonClass}
        >
          .
        </Button>
      </div>
    </div>
  );
};

export default NumericKeypad;
